import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayedUnit } from 'commons/src/commonFunctions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import { sensorFullNameKey } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { SelectedPeriod } from 'commons/src/models/commonTypeScript';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import SensorThresholdMgmt from '../../../account/thresholds/SensorThresholdMgmt';
import styles from './SpaceThresholdsModal.module.scss';
import useSpaceThresholdsModal from './useSpaceThresholdsModal';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    sensorType: SensorTypes;
    thresholds: SpaceThresholds;
    locationId: string;
    spaceId: string;
    selectedPeriod: SelectedPeriod;
};

const SpaceThresholdsModalComponent = ({
    onClose,
    sensorType,
    thresholds,
    locationId,
    spaceId,
    selectedPeriod,
    isOpen,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const { onSaveSuccess, redirectToBuildingThresholds } = useSpaceThresholdsModal({
        onClose,
        locationId,
        spaceId,
        selectedPeriod,
    });

    const unit = getDisplayedUnit(sensorType, thresholds.unit as string);

    const customHeader: ReactElement = (
        <div className={styles.header}>
            <div className={styles.headerSensor}>
                <SensorIcon sensor={sensorType} />
                <div>{`${txt('Space.SensorThresholds', {
                    sensor: txt(sensorFullNameKey(sensorType)),
                })} ${unit}`}</div>
            </div>
            <TertiaryButton onClick={onClose} noColor icon={<MaterialIcon name="close" data-testid="close-button" />} />
        </div>
    );

    return (
        <ModalWrapper isOpen={isOpen} onClose={onClose} customHeader={customHeader} size="small">
            <div className={styles.body}>
                <SensorThresholdMgmt
                    buttonText={txt(`CustomThresholds.ThresholdsText${ThresholdsResourceScope.SPACE}`)}
                    sensorType={sensorType}
                    thresholds={thresholds}
                    thresholdScope={ThresholdsResourceScope.SPACE}
                    parentThresholdScope={ThresholdsResourceScope.LOCATION}
                    id={spaceId}
                    parentUrl={redirectToBuildingThresholds}
                    updateSuccessAction={onSaveSuccess}
                />
            </div>
        </ModalWrapper>
    );
};

export default SpaceThresholdsModalComponent;
